import { createRootRoute, createRoute, createRouter, Outlet } from "@tanstack/react-router";
import { AppLayout } from "./components/AppLayout";
import { DashboardRoute } from "./routes/index";
import { OrganizationSettingsRoute } from "./routes/settings.organization";
import { MembersRoute } from "./routes/settings.members";
import { BillingRoute } from "./routes/settings.billing";
import { LicensesRoute } from "./routes/settings.licenses";
import { SessionsRoute } from "./routes/settings.sessions";
import { TokensRoute } from "./routes/settings.tokens";
import { ConnectedAppsRoute } from "./routes/settings.connected-apps";
import { AgentsRoute } from "./routes/settings.agents";
import { OAuthAuditRoute } from "./routes/settings.oauth-audit";
import { AdminOAuthResourcesRoute } from "./routes/admin.oauth-resources";
import { AdminOAuthClientsRoute } from "./routes/admin.oauth-clients";
import { AdminOAuthRegisteredClientsRoute } from "./routes/admin.oauth-registered-clients";
import { MarketplaceRoute } from "./routes/marketplace.index";
import { AdminOrganizationsRoute } from "./routes/admin.organizations";
import { AdminOrganizationDetailRoute } from "./routes/admin.organizations.$orgId";
import { AdminIssueLicenseRoute } from "./routes/admin.licenses.issue";
import { AdminConvertLicenseRoute } from "./routes/admin.licenses.$licenseId.convert";
import { RequirePlatformAdmin } from "./components/RequirePlatformAdmin";
import { LoginRoute } from "./routes/login";
import { RegisterRoute } from "./routes/register";
import { VerifyEmailRoute } from "./routes/verify-email";
import { ForgotPasswordRoute } from "./routes/forgot-password";
import { ResetPasswordRoute } from "./routes/reset-password";

const rootRoute = createRootRoute({
  component: () => (
    <AppLayout>
      <Outlet />
    </AppLayout>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: DashboardRoute,
});

const orgSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/organization",
  component: OrganizationSettingsRoute,
});

const membersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/members",
  component: MembersRoute,
});

const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/billing",
  component: BillingRoute,
});

const licensesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/licenses",
  component: LicensesRoute,
});

const tokensRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/tokens",
  component: TokensRoute,
});

const sessionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/sessions",
  component: SessionsRoute,
});

const connectedAppsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/connected-apps",
  component: ConnectedAppsRoute,
});

const agentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/agents",
  component: AgentsRoute,
});

const oauthAuditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/oauth-audit",
  component: OAuthAuditRoute,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginRoute,
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/register",
  component: RegisterRoute,
});

const marketplaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/marketplace",
  component: MarketplaceRoute,
});

// Every /admin/* route is wrapped in RequirePlatformAdmin -- UX-only
// defense in depth (the backend's own authsvc.RequirePlatformAdmin
// check is the real security boundary); AppLayout already hides the
// nav links to these for a non-admin, but hiding a link doesn't stop a
// direct URL visit.
const adminOrganizationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/organizations",
  component: () => (
    <RequirePlatformAdmin>
      <AdminOrganizationsRoute />
    </RequirePlatformAdmin>
  ),
});

const adminOrganizationDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/organizations/$orgId",
  component: () => (
    <RequirePlatformAdmin>
      <AdminOrganizationDetailRoute />
    </RequirePlatformAdmin>
  ),
});

const adminIssueLicenseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/licenses/issue",
  component: () => (
    <RequirePlatformAdmin>
      <AdminIssueLicenseRoute />
    </RequirePlatformAdmin>
  ),
});

const adminConvertLicenseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/licenses/$licenseId/convert",
  component: () => (
    <RequirePlatformAdmin>
      <AdminConvertLicenseRoute />
    </RequirePlatformAdmin>
  ),
});

const adminOAuthResourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/oauth-resources",
  component: () => (
    <RequirePlatformAdmin>
      <AdminOAuthResourcesRoute />
    </RequirePlatformAdmin>
  ),
});

const adminOAuthClientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/oauth-clients",
  component: () => (
    <RequirePlatformAdmin>
      <AdminOAuthClientsRoute />
    </RequirePlatformAdmin>
  ),
});

const adminOAuthRegisteredClientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/oauth-apps",
  component: () => (
    <RequirePlatformAdmin>
      <AdminOAuthRegisteredClientsRoute />
    </RequirePlatformAdmin>
  ),
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify-email",
  component: VerifyEmailRoute,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordRoute,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordRoute,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  orgSettingsRoute,
  membersRoute,
  billingRoute,
  licensesRoute,
  sessionsRoute,
  tokensRoute,
  connectedAppsRoute,
  agentsRoute,
  oauthAuditRoute,
  marketplaceRoute,
  adminOrganizationsRoute,
  adminOrganizationDetailRoute,
  adminIssueLicenseRoute,
  adminConvertLicenseRoute,
  adminOAuthResourcesRoute,
  adminOAuthClientsRoute,
  adminOAuthRegisteredClientsRoute,
  loginRoute,
  registerRoute,
  verifyEmailRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
